<script lang="ts">
  import { Toaster } from '$lib/components/ui/sonner';
  import { LoadingBar } from './(components)';
  import '../app.postcss';
  import { goto, invalidate, invalidateAll } from '$app/navigation';
  import { navigating, page } from '$app/stores';
  import { onDestroy, onMount } from 'svelte';
  import { QueryClientProvider } from '@tanstack/svelte-query';
  import { SvelteQueryDevtools } from '@tanstack/svelte-query-devtools';
  import { browser } from '$app/environment';
  import { setHooksContext } from '$lib/hooks/zenstack';
  import type { FetchFn } from '@zenstackhq/tanstack-query/runtime';
  import { ModeWatcher } from 'mode-watcher';
  import { toast } from 'svelte-sonner';
  import { sendPlausibleView } from '$lib/client/plausible';
  import { stripRouteIdsFromUrl } from '$lib/utils/stripRouteIdsFromUrl';
  import PlausibleAnalytics from './(components)/plausible-analytics.svelte';
  import { createDeviceInformationStore } from '$lib/hooks/device-information/store';
  import { setDeviceInformationContext } from '$lib/hooks/device-information/context';

  // custom fetch function that adds a custom header
  const myFetch: FetchFn = (url, options) => {
    options = options ?? {};
    options.headers = {
      ...options.headers
    };
    return fetch(url, options);
  };

  setHooksContext({
    endpoint: '/api/model',
    fetch: myFetch
  });

  onMount(() => {
    page.subscribe((value) => {
      let url = value.url.href;
      url = value.url.origin + value.route.id;
      url = stripRouteIdsFromUrl(url);
      // Remove () and make sure no multi slashes other than protocol. Example: http://localhost:3001/(private)/[accountId]/(cloudflare)/(synced)/content-policy/connect -> http://localhost:3001/accountId/content-policy/connect
      sendPlausibleView({
        u: url
      });
    });
  });

  export let data;
  $: ({ supabase, session, deviceType, testEnv } = data);

  const deviceInformationStore = createDeviceInformationStore({
    devicePayload: data.deviceType
  });
  setDeviceInformationContext(deviceInformationStore);

  const toastFromUrl = page.subscribe((value) => {
    if (!browser) {
      return;
    }
    const {
      url: { searchParams }
    } = value; // destructure query from $page store

    const title = searchParams.get('toast[title]');
    const message = searchParams.get('toast[description]') || searchParams.get('toast[message]');
    const type = searchParams.get('toast[type]') ?? 'info';
    const timeout = searchParams.get('toast[timeout]') ?? '5000';

    // Check if toast parameters exist in the URL
    if (title && message && type) {
      // Trigger the toast
      if (type === 'error' || type === 'fail') {
        toast.error(title, { description: message });
      } else if (type === 'success') {
        toast.success(title, { description: message });
      } else {
        toast.info(title, { description: message });
      }
    }
  });
  onDestroy(toastFromUrl);

  onMount(() => {
    const { data: supabaseData } = supabase.auth.onAuthStateChange((_, newSession) => {
      if (!newSession) {
        /**
         * Queue this as a task so the navigation won't prevent the
         * triggering function from completing
         */
        setTimeout(() => {
          goto('/', { invalidateAll: true });
        });
      }
      // prevent re-focusing the tab causing invalidation. This checks for the initial login event, but prevents refocusing the tab from calling this again. If we don't do this, the tab refocus causes invalidateAll to be called each time.
      if (_ === 'SIGNED_IN' && !session && newSession) {
        invalidateAll();
      }

      if (_ === 'SIGNED_OUT') {
        invalidateAll();
      }

      if (newSession?.expires_at !== session?.expires_at) {
        invalidate('supabase:auth');
      }
      // Other events we don't currently react to
      // 'INITIAL_SESSION'
      // 'PASSWORD_RECOVERY'
      // 'SIGNED_IN'
      // 'SIGNED_OUT'
      // 'TOKEN_REFRESHED'
      // 'USER_UPDATED'
      // invalidateAll();
    });

    return () => supabaseData.subscription.unsubscribe();
  });

  const isDev = $page.url.host.includes('localhost') || $page.url.host.includes('.black');
  const domain = isDev ? 'dashboard.techlockdown.black' : 'dashboard.techlockdown.com';
  const trackingScriptPath = isDev
    ? '/js/script.manual.outbound-links.local.js'
    : '/js/script.manual.outbound-links.js';
</script>

{#if Boolean($navigating)}
  <LoadingBar />
{/if}

<PlausibleAnalytics {isDev} {domain} {trackingScriptPath} />

<QueryClientProvider client={data.queryClient}>
  <ModeWatcher />

  <!-- <div class="relative flex min-h-screen flex-col bg-background">
    <div class="flex-1">
      <div class="container">
        <slot />
      </div>
    </div>

    <div class="pt-24">
      <Footer />
    </div>
  </div> -->

  <div class="bg-background relative flex min-h-screen flex-col">
    <slot />
  </div>
  <!-- <SvelteQueryDevtools buttonPosition="bottom-right" /> -->
</QueryClientProvider>

<Toaster
  richColors
  position={$deviceInformationStore.devicePayload.isDesktop ? 'top-center' : 'top-center'}
/>

{#if testEnv.isAnyTestValueEnabled}
  <div
    class="fixed bottom-4 right-0 transform translate-x-[calc(100%-20px)] hover:translate-x-0 transition-transform duration-300 ease-in-out bg-red-600 rounded-l-md shadow-md cursor-pointer"
  >
    <div class="px-4 py-2 text-white font-medium">A test value is enabled</div>
  </div>
{/if}
